import Modal from '@/components/Modal'
import { Button } from '@/components/ui/button'
import { ConnectorRecord } from '@/types/connector'
import { useAppStore } from '@/store'
import { useTranslation } from '@/i18n'
import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'

type ConnectorsModalProps = {
  isOpen: boolean
  onClose: () => void
}

const SQL_TYPE = 'sql'

const ConnectorsModal = ({ isOpen, onClose }: ConnectorsModalProps) => {
  const { t } = useTranslation()
  const { connectors, selectedDocuments, selectDocument, getDocuments, saveDocumentSelection } = useAppStore()
  const [isLoadingConnectors, setIsLoadingConnectors] = useState(false)
  const [isSavingOnClose, setIsSavingOnClose] = useState(false)
  const [initialSelectionIds, setInitialSelectionIds] = useState<string[]>([])

  // Refresh documents/connectors when modal opens and capture initial selection AFTER fetch completes
  // This prevents race conditions where initialSelectionIds captures [] before getDocuments populates selectedDocuments
  useEffect(() => {
    let isMounted = true

    if (isOpen) {
      const fetchLatestConnectors = async () => {
        setIsLoadingConnectors(true)
        try {
          await getDocuments()
          // Capture initial selection AFTER getDocuments completes to avoid race condition
          if (isMounted) {
            const { selectedDocuments: freshSelectedDocs } = useAppStore.getState()
            setInitialSelectionIds(freshSelectedDocs.map((doc: { document_id?: string; id?: string }) => doc.document_id ?? doc.id))
          }
        } finally {
          // Only update loading state if this effect hasn't been cleaned up
          if (isMounted) {
            setIsLoadingConnectors(false)
          }
        }
      }
      fetchLatestConnectors()
    } else {
      // Reset state when modal closes
      setIsLoadingConnectors(false)
      setInitialSelectionIds([])
    }

    // Cleanup function to prevent race conditions
    return () => {
      isMounted = false
    }
  }, [isOpen, getDocuments])

  // Check if document selection has changed since modal opened
  const hasSelectionChanged = () => {
    const currentIds = selectedDocuments.map((doc: { document_id?: string; id?: string }) => doc.document_id ?? doc.id)
    if (currentIds.length !== initialSelectionIds.length) return true
    const sortedCurrent = [...currentIds].sort()
    const sortedInitial = [...initialSelectionIds].sort()
    return sortedCurrent.some((id: string, index: number) => id !== sortedInitial[index])
  }

  // Handle "Close" button click - save pending changes then close
  const handleClose = async () => {
    if (hasSelectionChanged()) {
      setIsSavingOnClose(true)
      try {
        // Use showToast=false - the canvas auto-save will show the indicator
        // This avoids showing the indicator twice (once here, once from auto-save)
        const success = await saveDocumentSelection(false)
        if (success) {
          onClose() // Only close on successful save
        }
        // Keep modal open on save failure so user doesn't lose their changes
        // Error toast is already shown by saveBoard
      } finally {
        setIsSavingOnClose(false)
      }
    } else {
      onClose()
    }
  }

  // Only show loading for explicit modal save, not background auto-saves
  const isButtonLoading = isSavingOnClose

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('common:modals.select_connectors')}
      preventOutsideClose={isButtonLoading}
      footer={
        <Button
          variant="outline"
          onClick={handleClose}
          disabled={isButtonLoading}
        >
          {isButtonLoading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              {t('common:saving')}
            </>
          ) : (
            t('common:buttons.close')
          )}
        </Button>
      }
    >
      <div className="grid gap-4 py-4 relative min-h-[100px]">
        {isLoadingConnectors && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-dark-card/80 z-10 pointer-events-none">
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>{t('common:loading')}</span>
            </div>
          </div>
        )}
        {connectors
          .filter(({ type }) => type === SQL_TYPE)
          .map((connector: ConnectorRecord) => (
            <div key={connector.id} className="flex items-center gap-4">
              <input
                type="checkbox"
                id={connector.id}
                checked={selectedDocuments.some(
                  // TODO: Fix Types
                  /* eslint-disable  @typescript-eslint/no-explicit-any */
                  (selectedDoc: any) => selectedDoc.document_id === connector.id
                )}
                onChange={(e) =>
                  selectDocument(connector.id, e.target.checked, true)
                }
              />
              <label htmlFor={connector.id}>{connector.name}</label>
            </div>
          ))}
      </div>
    </Modal>
  )
}

export default ConnectorsModal
